import { format } from "date-fns";

import type {
  AcknowledgementItemSummaryDto,
  AcknowledgementProgressDto,
  AcknowledgementRosterRowDto,
} from "@/generated/model";
import {
  AcknowledgementItemSummaryDtoReminderFrequency,
  AcknowledgementRosterRowDtoState,
} from "@/generated/model";
import { dueLabel } from "@/lib/acknowledgements/my-acknowledgements";

/**
 * The five summary tiles (AC25).
 *
 * AC25's wording — "Staff Required, Read and Signed, Completion percentage,
 * Outstanding" — reads as four labels, but AC15 requires Read and Signed to be
 * reported separately, so it is five. Flagged for confirmation at sign-off.
 */
export interface TrackingTile {
  key: "staffRequired" | "read" | "signed" | "completion" | "outstanding";
  label: string;
  value: number;
  /** Rendered as a percentage rather than a count. */
  isPercentage?: boolean;
}

export function trackingTiles(
  progress: AcknowledgementProgressDto,
): TrackingTile[] {
  return [
    {
      key: "staffRequired",
      label: "Staff required",
      value: progress.staffRequired,
    },
    { key: "read", label: "Read", value: progress.read },
    { key: "signed", label: "Signed", value: progress.signed },
    {
      key: "completion",
      label: "Completion",
      value: progress.completionPercentage,
      isPercentage: true,
    },
    { key: "outstanding", label: "Outstanding", value: progress.outstanding },
  ];
}

/** AC26's panel: everyone still to sign, in the order the roster gives them. */
export function outstandingRows(
  roster: AcknowledgementRosterRowDto[],
): AcknowledgementRosterRowDto[] {
  return roster.filter(
    (row) => row.state === AcknowledgementRosterRowDtoState.OUTSTANDING,
  );
}

/**
 * AC27's "View Outstanding Only" toggle, applied client-side.
 *
 * The roster arrives whole in one payload, so filtering here avoids a refetch —
 * and keeps the tiles, which describe the whole publication, honest while the
 * table below them is narrowed.
 */
export function filterRoster(
  roster: AcknowledgementRosterRowDto[],
  outstandingOnly: boolean,
): AcknowledgementRosterRowDto[] {
  return outstandingOnly ? outstandingRows(roster) : roster;
}

/**
 * A fixed format, rendered in the viewer's LOCAL timezone. Shared by the
 * tracking table and the CSV export so the two never disagree.
 *
 * Local rather than UTC on purpose: these are UK nursery records read by
 * managers and inspectors, and "signed at 09:00" needs to mean the time on the
 * clock in the building. The format string is fixed so the column is sortable
 * and unambiguous regardless of locale settings.
 */
export function formatAcknowledgementDateTime(
  value: string | Date | null | undefined,
): string | null {
  return formatLocal(value, "yyyy-MM-dd HH:mm");
}

/**
 * Date only, for the due date: it is picked as a day, so a time would be one
 * nobody chose.
 */
export function formatAcknowledgementDate(
  value: string | Date | null | undefined,
): string | null {
  return formatLocal(value, "yyyy-MM-dd");
}

function formatLocal(
  value: string | Date | null | undefined,
  pattern: string,
): string | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return format(date, pattern);
}

/** `empty` means `text` is a label standing in for a missing date. */
export interface RosterDateCell {
  text: string;
  empty: boolean;
}

export interface RosterDueCell extends RosterDateCell {
  overdue: boolean;
  /** How late, e.g. "3 day(s) overdue". Null unless overdue. */
  overdueText: string | null;
}

/** The date columns of one tracking-table row (AC24). */
export function rosterDateCells(
  row: AcknowledgementRosterRowDto,
  now: Date,
): {
  due: RosterDueCell;
  read: RosterDateCell;
  signed: RosterDateCell;
  lastReminded: RosterDateCell;
  nextReminder: RosterDateCell;
} {
  return {
    due: dueCell(row, now),
    read: readCell(row),
    signed: dateCell(row.signedAt, "Not signed"),
    lastReminded: dateCell(row.lastRemindedAt, "Never"),
    nextReminder: nextReminderCell(row),
  };
}

function dateCell(value: string | null, emptyText: string): RosterDateCell {
  const text = formatAcknowledgementDateTime(value);
  if (!text) {
    return { text: emptyText, empty: true };
  }
  return { text, empty: false };
}

/**
 * Only an outstanding row can be overdue: someone who signed late, or whose
 * request was voided, has nothing left to chase.
 */
function dueCell(row: AcknowledgementRosterRowDto, now: Date): RosterDueCell {
  const text = formatAcknowledgementDate(row.dueAt);
  if (!text) {
    return {
      text: "No due date",
      empty: true,
      overdue: false,
      overdueText: null,
    };
  }
  const notOverdue = { text, empty: false, overdue: false, overdueText: null };
  if (row.state !== AcknowledgementRosterRowDtoState.OUTSTANDING) {
    return notOverdue;
  }
  const due = dueLabel(row, now);
  if (!due.overdue) {
    return notOverdue;
  }
  return { text, empty: false, overdue: true, overdueText: due.text };
}

/**
 * Signing now stamps Read at the same moment, so a signed row with no read date
 * was migrated from the pre-NAU-410 model, which never captured Read. "Not
 * read" would claim they signed without reading.
 */
function readCell(row: AcknowledgementRosterRowDto): RosterDateCell {
  const text = formatAcknowledgementDateTime(row.readAt);
  if (text) {
    return { text, empty: false };
  }
  if (row.state === AcknowledgementRosterRowDtoState.SIGNED) {
    return { text: "Not recorded", empty: true };
  }
  return { text: "Not read", empty: true };
}

/**
 * The API only derives a next reminder for outstanding rows, so a null there
 * means no frequency was set; anywhere else there is nobody left to remind.
 */
function nextReminderCell(row: AcknowledgementRosterRowDto): RosterDateCell {
  const text = formatAcknowledgementDateTime(row.nextReminderDueAt);
  if (text) {
    return { text, empty: false };
  }
  if (row.state === AcknowledgementRosterRowDtoState.OUTSTANDING) {
    return { text: "No reminders", empty: true };
  }
  return { text: "Not needed", empty: true };
}

const REMINDER_FREQUENCY_LABEL: Record<
  AcknowledgementItemSummaryDtoReminderFrequency,
  string
> = {
  [AcknowledgementItemSummaryDtoReminderFrequency.NONE]: "None",
  [AcknowledgementItemSummaryDtoReminderFrequency.DAILY]: "Daily",
  [AcknowledgementItemSummaryDtoReminderFrequency.WEEKLY]: "Weekly",
};

export function reminderFrequencyLabel(
  frequency: AcknowledgementItemSummaryDtoReminderFrequency | string,
): string {
  return (
    REMINDER_FREQUENCY_LABEL[
      frequency as AcknowledgementItemSummaryDtoReminderFrequency
    ] ?? frequency
  );
}

export function trackingHeaderLine(
  item: Pick<
    AcknowledgementItemSummaryDto,
    "version" | "publishedByName" | "dueAt" | "reminderFrequency"
  >,
): string {
  const parts = [`Version ${item.version}`];
  if (item.publishedByName) {
    parts.push(`sent by ${item.publishedByName}`);
  }
  const due = formatAcknowledgementDate(item.dueAt);
  if (due) {
    parts.push(`Due by ${due}`);
  }
  parts.push(`Reminders: ${reminderFrequencyLabel(item.reminderFrequency)}`);
  return parts.join(" · ");
}

/**
 * Can this person be chased right now?
 *
 * Mirrors the server's rules so the button is disabled rather than producing a
 * 409: signed people need no chasing, superseded requests are not work, and
 * there is a 12-hour cooling-off window between reminders.
 */
export const REMINDER_COOLDOWN_HOURS = 12;

export function canRemind(
  row: AcknowledgementRosterRowDto,
  now: Date,
): boolean {
  if (row.state !== AcknowledgementRosterRowDtoState.OUTSTANDING) {
    return false;
  }
  if (!row.lastRemindedAt) {
    return true;
  }
  const elapsedHours =
    (now.getTime() - new Date(row.lastRemindedAt).getTime()) / (60 * 60 * 1000);
  return elapsedHours >= REMINDER_COOLDOWN_HOURS;
}

/** How many of the outstanding people "Send reminder to all" would actually chase. */
export function remindableCount(
  roster: AcknowledgementRosterRowDto[],
  now: Date,
): number {
  return roster.filter((row) => canRemind(row, now)).length;
}

/**
 * Master switch for the reminder controls: the per-row Remind button in the
 * tracking table and the "Remind N people" button on the panel.
 *
 * Off until notification delivery is built properly. A reminder currently sends
 * nothing — no email, no push, and nothing on a schedule — so pressing Remind
 * only stamps a date, which promises the manager an action that never reaches
 * the person. Better to show nothing than a button that quietly does nothing.
 *
 * The rest of the reminder path is complete: endpoints, the cooldown, the
 * last-reminded date and the CSV column all work. So this hides the controls
 * rather than removing them, and flipping this back to true is the whole of
 * restoring the feature once reminders actually deliver.
 *
 * Typed as `boolean` rather than inferred as `false` so neither TypeScript nor
 * ESLint treats the guarded markup as dead code.
 */
export const REMINDER_ACTIONS_ENABLED: boolean = false;
